<template>
  <v-container fluid class="pa-6">
    <div class="d-flex justify-space-between align-center mb-6">
      <h1 class="text-h5 font-weight-bold text-grey-darken-4">
        Lista de Planos de Assinatura
      </h1>
      <v-btn color="primary" @click="handleCreateSubscription">
        <v-icon start>mdi-plus</v-icon>
        Novo Plano
      </v-btn>
    </div>

    <v-card class="elevation-4">
      <v-card-text>
        <v-row class="mb-6" dense>
          <v-col cols="12" md="6">
            <v-text-field v-model="searchTerm" placeholder="Pesquisar por nome ou código" clearable hide-details
              prepend-inner-icon="mdi-magnify" @input="onFiltersChange" />
          </v-col>
          <v-col cols="12" md="6">
            <v-select v-model="selectedStatus" :items="statusOptions" placeholder="Filtrar por Status" clearable
              hide-details @update:model-value="onFiltersChange" />
          </v-col>
          <v-col cols="12" md="4">
            <v-select v-model="selectedEmployabilityPlan" :items="employabilityPlanOptions"
              placeholder="Filtrar por Empregabilidade" clearable hide-details @update:model-value="onFiltersChange" />
          </v-col>
        </v-row>

        <v-data-table-server :headers="columns" :items="subscriptions" :items-length="totalItems"
          :loading="isLoading" :items-per-page="itemsPerPage" :page="page" :no-data-text="noDataMessage"
          class="elevation-0" @update:page="page = $event" @update:items-per-page="itemsPerPage = $event">
          <template v-slot:item.code="{ item }">
            <span class="font-weight-medium">{{ item.code }}</span>
          </template>

          <template v-slot:item.amount="{ item }">
            <span>R$
              {{
                parseFloat(item.amount).toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                })
              }}</span>
          </template>

          <template v-slot:item.plan_type="{ item }">
            <v-chip :color="planTypeTagMap[item.plan_type]" label>
              {{ formatPlanType(item.plan_type) }}
            </v-chip>
          </template>

          <template v-slot:item.is_active="{ item }">
            <v-chip :color="isActiveTagMap[item.is_active]" label>
              {{ formatIsActive(item.is_active) }}
            </v-chip>
          </template>

          <template #item.is_employability_plan="{ item }">
            <v-chip :color="item.is_employability_plan ? 'primary' : 'grey'">
              {{ item.is_employability_plan ? 'Sim' : 'Não' }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-icon icon="mdi-pencil" color="warning" size="large" class="mr-2" @click="handleEdit(item)"
              title="Editar Plano" />
            <v-icon icon="mdi-delete" color="error" size="large" @click="handleDelete(item)" title="Excluir Plano" />
          </template>
        </v-data-table-server>

        <v-alert v-if="hasError && !isLoading" type="error" variant="tonal" class="mt-4 text-center">
          Ocorreu um erro ao carregar os dados dos planos.
        </v-alert>
      </v-card-text>
    </v-card>

    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h5">Confirmar Exclusão</v-card-title>
        <v-card-text>
          Tem certeza de que deseja excluir o plano
          <strong>{{ itemToDelete.name }}</strong>?
          <br /><br />
          Código: <strong>{{ itemToDelete.code }}</strong>
          <p class="mt-2 text-error">
            Esta ação é **irreversível**.
          </p>
        </v-card-text>
        <v-card-actions class="d-flex justify-space-between">
          <v-btn color="secondary" variant="flat" @click="deleteDialog = false">
            Cancelar
          </v-btn>
          <v-btn color="error" variant="flat" :loading="isDeleting" @click="confirmDelete">
            Excluir Plano
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/services/api'
import { useToastStore } from '@/store/toast'
import { useLoadingStore } from '@/store/loadingStore'

const router = useRouter()
const toastStore = useToastStore()
const loadingStore = useLoadingStore()

// Estado dos Dados
const subscriptions = ref([])
const isLoading = ref(true)
const hasError = ref(false)
const isDeleting = ref(false)
const initialized = ref(false)

const searchTerm = ref('')
const selectedStatus = ref(1)
const selectedEmployabilityPlan = ref(null)

const page = ref(1)
const itemsPerPage = ref(10)
const totalItems = ref(0)

const deleteDialog = ref(false)
const itemToDelete = ref({})

const statusOptions = [
  { title: 'Ativo', value: 1 },
  { title: 'Inativo', value: 0 },
]

const employabilityPlanOptions = [
  { title: 'Sim', value: 1 },
  { title: 'Não', value: 0 },
]

const isActiveTagMap = {
  0: 'error',
  1: 'success',
}

const planTypeTagMap = {
  monthly: 'blue-grey',
  'semi-annual': 'teal',
  annual: 'indigo',
}

const noDataMessage = computed(() => {
  if (isLoading.value) return 'Carregando...'
  return 'Nenhum plano de assinatura encontrado com os filtros aplicados.'
  return ''
})

const columns = [
  { title: 'Código', key: 'code', sortable: true },
  { title: 'Nome do Plano', key: 'name', sortable: true },
  { title: 'Tipo', key: 'plan_type', sortable: true },
  { title: 'Valor', key: 'amount', sortable: true },
  { title: 'Ativo', key: 'is_active', sortable: true },
  { title: 'Empregabilidade', key: 'is_employability_plan', sortable: true },
  { title: 'Ações', key: 'actions', sortable: false },
]

const loadSubscriptions = async () => {
  isLoading.value = true
  hasError.value = false
  loadingStore.show()
  try {
    const params = {
      page: page.value,
      per_page: itemsPerPage.value,
      search: searchTerm.value || null,
      is_active: selectedStatus.value ?? null,
      is_employability_plan: selectedEmployabilityPlan.value ?? null,
    }
    const response = await api.get('/subscriptions', { params })

    subscriptions.value = response.data.data || []

    totalItems.value = response.data.total
    itemsPerPage.value = response.data.per_page
    page.value = response.data.current_page
  } catch (error) {
    console.error('Erro ao carregar planos:', error)
    hasError.value = true
    toastStore.showToast({
      message:
        error?.response?.data?.error ||
        'Erro ao carregar a lista de planos de assinatura.',
      color: 'error',
    })
  } finally {
    isLoading.value = false
    loadingStore.hide()
  }
}

// Monitora todos os parâmetros que afetam a requisição
watch(
  [page, itemsPerPage, searchTerm, selectedStatus, selectedEmployabilityPlan],
  () => {
    if (!initialized.value) return
    loadSubscriptions()
  }
)

function onFiltersChange() {
    page.value = 1 
}

const handleCreateSubscription = () => {
  router.push('/app/assinaturas/novo')
}

const handleEdit = async (row) => {
  router.push({ name: 'subscription-edit', params: { id: row.id } })
}

const handleDelete = (item) => {
  itemToDelete.value = item
  deleteDialog.value = true
}

const confirmDelete = async () => {
  isDeleting.value = true
  try {
    await api.delete(`/subscriptions/${itemToDelete.value.id}`)
    toastStore.showToast({
      message: 'Plano excluído com sucesso!',
      color: 'success',
    })
    await loadSubscriptions()
    deleteDialog.value = false
  } catch (error) {
    console.error('Erro ao excluir plano:', error)
    toastStore.showToast({
      message:
        error?.response?.data?.error || 'Erro ao excluir o plano de assinatura.',
      color: 'error',
    })
  } finally {
    isDeleting.value = false
  }
}

function formatPlanType(type) {
  const map = {
    monthly: 'Mensal',
    'semi-annual': 'Semestral',
    annual: 'Anual',
  }
  return map[type] || 'Outro'
}

function formatIsActive(is_active) {
  return is_active === 1 ? 'Ativo' : 'Inativo'
}

function formatDate(dateString) {
  if (!dateString) return 'N/A'
  const date = new Date(dateString)
  // Formato: DD/MM/AAAA
  return date.toLocaleDateString('pt-BR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

onMounted(async () => {
  await loadSubscriptions()
  initialized.value = true
})
</script>